import axios from 'axios';
import { MNSproCloudApi } from '..';
import {
  BasicDeviceResponse,
  BitLockerRecoveryKeyResponse,
  DeviceOwner,
  DeviceRegistration,
  PaginatedOfBasicDeviceResponse,
} from '../api/v2';

export class DeviceService {
  /**
   * Fetches the first page of the organization's device list.
   *
   * @param authToken MNSpro Cloud access token.
   * @param organizationId Organization whose devices are listed.
   * @returns The page's devices, the link to the next page (if any) and the
   * total number of devices behind the whole list, which the caller needs to
   * show the final list length before every page has arrived.
   */
  static async getDevices(authToken: string, organizationId: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const response = await api.getAllBasicDevices(
      organizationId,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      500,
    );
    let nextLink: string | null | undefined = response.data.nextLink;
    let devices: BasicDeviceResponse[] = response.data.value;

    return { devices, nextLink, totalCount: response.data.totalCount };
  }

  /**
   * Fetches the first page of the device list matching a search term.
   *
   * @param authToken MNSpro Cloud access token.
   * @param organizationId Organization whose devices are listed.
   * @param searchquery Term matched against name, serial number and id.
   * @returns The page's devices, the link to the next page (if any) and the
   * total number of matches behind the whole list.
   */
  static async getFilteredDevices(
    authToken: string,
    organizationId: string,
    searchquery: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const response = await api.getAllBasicDevices(
      organizationId,
      undefined,
      undefined,
      searchquery,
      undefined,
      undefined,
      500,
    );
    let nextLink: string | null | undefined = response.data.nextLink;
    let devices: BasicDeviceResponse[] = response.data.value;

    return { devices, nextLink, totalCount: response.data.totalCount };
  }

  /**
   * Follows a device list page link handed out by a previous call.
   *
   * @param authToken MNSpro Cloud access token.
   * @param nextLink Absolute link taken from the previous page's `nextLink`.
   * @param abortSignal Signal aborting the request, or `null` to keep it running.
   * @returns The page's devices, the link to the next page (if any) and the
   * total number of devices behind the whole list.
   */
  static async getNextLinkDevices(
    authToken: string,
    nextLink: string,
    abortSignal: AbortSignal | null,
  ) {
    const pageResponse = await axios.get<PaginatedOfBasicDeviceResponse>(
      nextLink,
      {
        headers: {
          authorization: 'Bearer ' + authToken,
        },
        signal: abortSignal === null ? undefined : abortSignal,
      },
    );

    return {
      devices: pageResponse.data.value,
      nextLink: pageResponse.data.nextLink,
      totalCount: pageResponse.data.totalCount,
    };
  }

  static async getUnmanagedDevices(authToken: string, organizationId: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const response = await api.getAllBasicDevices(
      organizationId,
      undefined,
      DeviceRegistration.registered,
      undefined,
      undefined,
      20,
    );
    let nextLink: string | null | undefined = response.data.nextLink;
    let devices: BasicDeviceResponse[] = response.data.value;

    while (nextLink) {
      const pageResponse = await axios.get<PaginatedOfBasicDeviceResponse>(
        nextLink,
        {
          headers: {
            authorization: 'Bearer ' + authToken,
          },
        },
      );
      devices = devices.concat(pageResponse.data.value);
      nextLink = pageResponse.data.nextLink;
    }

    return devices;
  }

  static async getDeviceById(
    authToken: string,
    deviceId: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.getDevice(deviceId, organizationId);
    return device.data;
  }

  static async deviceChangeDescription(
    authToken: string,
    deviceId: string,
    description: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.applyChangeDescriptionAction(
      deviceId,
      organizationId,
      description,
    );
    return device.data;
  }

  static async getEnrollmentProfiles(
    authToken: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);
    // needs a change after implementation backend
    const device = await api.getAllBasicDevices(organizationId);
    return device.data;
  }

  static async deviceCleanWindows(
    authToken: string,
    deviceId: string,
    keepUserData: boolean,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.applyCleanWindowsDeviceAction(
      deviceId,
      organizationId,
      keepUserData,
    );
    return device.data;
  }

  static async deviceChangeOwner(
    authToken: string,
    deviceId: string,
    deviceOwner: DeviceOwner,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.applyDeviceActionChangeOwnership(
      deviceId,
      organizationId,
      deviceOwner,
    );
    return device.data;
  }

  static async deviceReboot(
    authToken: string,
    deviceId: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.applyRebootAction(deviceId, organizationId);
    return device.data;
  }

  static async deviceRename(
    authToken: string,
    deviceId: string,
    deviceName: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.applyRenameDeviceAction(
      organizationId,
      deviceId,
      deviceName,
    );

    return device.data;
  }

  static async deviceResetPasscode(
    authToken: string,
    deviceId: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.applyResetPasscodeAction(deviceId, organizationId);
    return device.data;
  }

  static async deviceRetire(
    authToken: string,
    deviceId: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.applyRetireAction(deviceId, organizationId);
    return device.data;
  }

  static async deviceSync(
    authToken: string,
    deviceId: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.applySyncAction(deviceId, organizationId);
    return device.data;
  }

  static async deviceWipe(
    authToken: string,
    deviceId: string,
    organizationId: string,
    keepUserData: boolean,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.applyWipeAction(
      deviceId,
      organizationId,
      keepUserData,
    );
    return device.data;
  }

  static async deviceRemoteLock(
    authToken: string,
    deviceId: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.applyRemoteLockDeviceAction(
      deviceId,
      organizationId,
    );
    return device.data;
  }

  static async deviceBitlockerWindows(
    authToken: string,
    deviceId: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.getBitlockerKeys(organizationId, deviceId);
    return device.data as unknown as BitLockerRecoveryKeyResponse[];
  }

  static async deviceByPassCodeMac(
    authToken: string,
    deviceId: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.getIosBypassCode(deviceId, organizationId);
    return device.data;
  }

  static async deviceWipeMacOs(
    authToken: string,
    deviceId: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.applyWipeAction(deviceId, organizationId);
    return device.data;
  }

  static async deviceEnableLostMode(
    authToken: string,
    deviceId: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.applyEnableLostModeAction(
      deviceId,
      organizationId,
    );
    return device.data;
  }

  static async deviceDisableLostMode(
    authToken: string,
    deviceId: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.applyDisableLostModeAction(
      deviceId,
      organizationId,
    );
    return device.data;
  }

  static async deviceLocate(
    authToken: string,
    deviceId: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const device = await api.applyLocateDeviceAction(deviceId, organizationId);
    return device.data;
  }
}
